import Phaser from 'phaser';
import type { RunData } from './Game';
import { t, tWithFallback, toggleLang } from '../../i18n';
import { hapticImpactLight, hapticSuccess, setTelegramBackButton, shareRunScore } from '../../telegram/webApp';

const API_BASE = '/api';

interface CardDrop {
  id: string;
  name: string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  description: string;
}

interface RunEndResponse {
  gold_earned: number;
  xp_earned: number;
  xp_total: number;
  gold_total: number;
  level_up: boolean;
  card_drop: CardDrop | null;
}

const RARITY_COLORS: Record<string, number> = {
  common: 0x888888,
  rare: 0x3399ff,
  epic: 0xaa44ff,
  legendary: 0xffaa00,
};

/**
 * Results scene — shown after the run ends.
 * Receives RunData via scene data, calls /api/run/end, displays rewards.
 */
export class ResultsScene extends Phaser.Scene {
  private runData!: RunData;
  private rewards: RunEndResponse | null = null;

  constructor() {
    super('Results');
  }

  init(data: RunData): void {
    this.runData = data;
  }

  create(): void {
    const { width, height } = this.scale;

    this.cameras.main.setBackgroundColor('#0a0f1e');
    this.cameras.main.fadeIn(220, 0, 0, 0);

    // Telegram: back button returns to Menu
    setTelegramBackButton(() => {
      this.cameras.main.fadeOut(220, 0, 0, 0);
      this.time.delayedCall(220, () => this.scene.start('Menu'));
    });

    // Fetch rewards from server, then render
    this.fetchRewards()
      .then((rewards) => {
        this.rewards = rewards;
        this.renderResults(width, height);
      })
      .catch(() => {
        // Render without server rewards on failure
        this.rewards = null;
        this.renderResults(width, height);
      });
  }

  private async fetchRewards(): Promise<RunEndResponse> {
    const body = {
      tg_user_id: 0, // Telegram user id placeholder (no auth yet)
      duration: this.runData.duration,
      wave_reached: this.runData.waveReached,
      enemies_killed: this.runData.enemiesKilled,
    };

    const res = await fetch(`${API_BASE}/run/end`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) throw new Error(`Server error: ${res.status}`);
    const json = (await res.json()) as { success: boolean; data: RunEndResponse };
    if (!json.success || !json.data) throw new Error('Invalid response');
    return json.data;
  }

  private renderResults(width: number, height: number): void {
    const panelW = Math.min(width - 32, 380);
    const panelX = (width - panelW) / 2;
    const hasCard = this.rewards?.card_drop != null;
    const panelH = hasCard ? 520 : 400;
    const panelY = (height - panelH) / 2;

    // Background dim
    this.add.rectangle(0, 0, width, height, 0x000000, 0.7).setOrigin(0, 0);

    // Panel
    const panel = this.add.graphics();
    panel.fillStyle(0x0d1b2a, 1);
    panel.fillRoundedRect(panelX, panelY, panelW, panelH, 14);
    panel.lineStyle(2, 0x00aaff, 0.9);
    panel.strokeRoundedRect(panelX, panelY, panelW, panelH, 14);

    let y = panelY + 24;

    // Title
    this.add
      .text(width / 2, y, t('results.title'), {
        fontSize: '26px',
        color: '#ff4444',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 4,
      })
      .setOrigin(0.5, 0);
    y += 46;

    // Stat rows
    const formatTime = (s: number) =>
      `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

    const stats: Array<[string, string]> = [
      [t('results.survived'), formatTime(this.runData.duration)],
      [t('results.wave'), String(this.runData.waveReached)],
      [t('results.kills'), String(this.runData.enemiesKilled)],
      [t('results.coins'), `+${this.runData.coinsEarned}`],
    ];

    if (this.rewards) {
      stats.push([t('results.gold'), `+${this.rewards.gold_earned}`]);
      stats.push([t('results.xp'), `+${this.rewards.xp_earned}`]);
    }

    for (const [label, value] of stats) {
      this.add
        .text(panelX + 20, y, label, {
          fontSize: '16px',
          color: '#aaccee',
        });
      this.add
        .text(panelX + panelW - 20, y, value, {
          fontSize: '16px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(1, 0);
      y += 32;
    }

    if (this.rewards?.level_up) {
      y += 4;
      this.add
        .text(width / 2, y, t('results.levelup'), {
          fontSize: '20px',
          color: '#ffdd00',
          fontStyle: 'bold',
          stroke: '#000000',
          strokeThickness: 3,
        })
        .setOrigin(0.5, 0);
      y += 36;
      hapticSuccess();
    }

    // Card drop section
    if (this.rewards?.card_drop) {
      y += 8;
      this.renderCardDrop(this.rewards.card_drop, panelX, panelW, y);
      y += 130;
      hapticImpactLight();
    }

    // Buttons
    y = panelY + panelH - 70;

    const btnW = Math.floor((panelW - 48) / 2);

    this.makeButton(panelX + 16, y, btnW, 44, t('results.play_again'), 0x1a4a2a, 0x00cc66, () => {
      hapticImpactLight();
      this.transitionToScene('Game');
    });

    this.makeButton(panelX + 16 + btnW + 16, y, btnW, 44, t('results.lobby'), 0x1a2a4a, 0x3399ff, () => {
      hapticImpactLight();
      this.transitionToLobby();
    });

    // Share button — below the two action buttons
    const shareBtnY = y + 52;
    this.makeButton(panelX + 16, shareBtnY, panelW - 32, 36, t('results.share'), 0x1a3a1a, 0x00aa44, () => {
      hapticImpactLight();
      const msg = t('results.share_message')
        .replace('{wave}', String(this.runData.waveReached))
        .replace('{kills}', String(this.runData.enemiesKilled))
        .replace('{time}', `${Math.floor(this.runData.duration / 60)}:${(this.runData.duration % 60).toString().padStart(2, '0')}`);
      shareRunScore(msg);
    });

    const langBtn = this.add
      .text(width - 16, panelY + 12, t('menu.lang'), {
        fontSize: '14px',
        color: '#ffffff',
        backgroundColor: '#16213e',
        padding: { x: 10, y: 6 },
      })
      .setOrigin(1, 0)
      .setInteractive({ useHandCursor: true });

    langBtn.on('pointerdown', () => {
      toggleLang();
      this.scene.restart(this.runData);
    });
  }

  private renderCardDrop(card: CardDrop, panelX: number, panelW: number, y: number): void {
    const cardW = Math.min(panelW - 40, 220);
    const cardH = 110;
    const cardX = panelX + (panelW - cardW) / 2;
    const borderColor = RARITY_COLORS[card.rarity] ?? 0x888888;
    const translatedName = this.translateCardField(card.id, 'name', card.name);
    const translatedDesc = this.translateCardField(card.id, 'description', card.description);

    const cardBg = this.add.graphics();
    cardBg.fillStyle(0x16213e, 0);
    cardBg.fillRoundedRect(cardX, y, cardW, cardH, 10);
    cardBg.lineStyle(2, borderColor, 0);
    cardBg.strokeRoundedRect(cardX, y, cardW, cardH, 10);
    cardBg.setAlpha(0);

    const label = this.add
      .text(panelX + panelW / 2, y - 4, t('results.card_drop'), {
        fontSize: '15px',
        color: '#ffdd44',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 2,
      })
      .setOrigin(0.5, 1)
      .setAlpha(0);

    const cardName = this.add
      .text(cardX + cardW / 2, y + 30, translatedName, {
        fontSize: '16px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    const cardDesc = this.add
      .text(cardX + cardW / 2, y + 60, translatedDesc, {
        fontSize: '12px',
        color: '#aaccee',
        wordWrap: { width: cardW - 16 },
        align: 'center',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    const rarityHex = `#${borderColor.toString(16).padStart(6, '0')}`;
    const rarityLabel = this.add
      .text(cardX + cardW / 2, y + cardH - 14, t(`rarity.${card.rarity}`), {
        fontSize: '10px',
        color: rarityHex,
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 1)
      .setAlpha(0);

    // Reveal animation
    this.time.delayedCall(300, () => {
      this.tweens.add({
        targets: [cardBg, label, cardName, cardDesc, rarityLabel],
        alpha: 1,
        duration: 600,
        ease: 'Power2',
        onUpdate: () => {
          // Redraw with full opacity once visible
          cardBg.clear();
          cardBg.fillStyle(0x16213e, 1);
          cardBg.fillRoundedRect(cardX, y, cardW, cardH, 10);
          cardBg.lineStyle(2, borderColor, 1);
          cardBg.strokeRoundedRect(cardX, y, cardW, cardH, 10);
        },
      });
    });
  }

  private makeButton(
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    bgColor: number,
    borderColor: number,
    onClick: () => void,
  ): void {
    const bg = this.add.graphics();
    const drawBg = (hover: boolean) => {
      bg.clear();
      bg.fillStyle(hover ? borderColor : bgColor, 1);
      bg.fillRoundedRect(x, y, w, h, 8);
      bg.lineStyle(2, borderColor, 1);
      bg.strokeRoundedRect(x, y, w, h, 8);
    };
    drawBg(false);

    const txt = this.add
      .text(x + w / 2, y + h / 2, label, {
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const zone = this.add
      .zone(x, y, w, h)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });

    zone.on('pointerover', () => { drawBg(true); txt.setColor('#000000'); });
    zone.on('pointerout', () => { drawBg(false); txt.setColor('#ffffff'); });
    zone.on('pointerdown', onClick);
  }

  private transitionToScene(sceneKey: string): void {
    this.cameras.main.fadeOut(220, 0, 0, 0);
    this.time.delayedCall(220, () => this.scene.start(sceneKey));
  }

  private transitionToLobby(): void {
    this.cameras.main.fadeOut(220, 0, 0, 0);
    this.time.delayedCall(220, () => {
      window.location.href = '/lobby';
    });
  }

  private translateCardField(cardId: string, field: 'name' | 'description', fallback: string): string {
    return tWithFallback(`card.${cardId}.${field}`, fallback);
  }
}
